/**
 * StatCalculator - Unified modifier aggregation and calculation system
 *
 * Collects modifiers from all sources:
 * - Equipped items (implicit, prefix, suffix mods)
 * - Passive tree bonuses
 * - Skill effect stones
 * - Active buffs/debuffs
 * - Base stats
 *
 * Provides final calculated values for all stats using POE-style calculation.
 */

import { ModifierCollection, Modifier, StatType, ModifierType } from './ModifierSystem';
import { EquipmentItem, ItemMod, ModifierCalcType } from '@shared/types';

// ========================
// Trigger Types
// ========================

/**
 * When the effect triggers
 */
export type TriggerType =
  | 'passive'           // Always active
  | 'on_hit'            // When dealing damage
  | 'on_crit'           // When landing a critical hit
  | 'on_kill'           // When killing an enemy
  | 'on_take_damage'    // When receiving damage
  | 'on_block'          // When blocking an attack
  | 'on_dodge'          // When dodging an attack
  | 'on_skill_use'      // When using a skill
  | 'on_skill_hit'      // When skill hits an enemy
  | 'on_low_hp'         // When HP drops below threshold (30%)
  | 'on_full_hp'        // When at full HP
  | 'on_flask_use';     // When using a flask/potion

/**
 * Effect that can be triggered
 */
export interface TriggerEffect {
  id: string;
  name: string;
  trigger: TriggerType;

  // Condition (optional)
  condition?: {
    hpThreshold?: number;      // Trigger if HP below this %
    manaThreshold?: number;    // Trigger if mana below this %
    chance?: number;           // Probability 0-100
    cooldown?: number;         // Seconds between triggers
    elementType?: string;      // Only for specific element
    skillType?: string;        // Only for specific skill type
  };

  // Effect
  effect: TriggerEffectAction;

  // Source tracking
  source?: string;
}

/**
 * What happens when triggered
 */
export type TriggerEffectAction =
  | { type: 'apply_modifier'; modifier: Modifier; duration?: number }
  | { type: 'heal'; amount: number; isPercent?: boolean }
  | { type: 'restore_mana'; amount: number; isPercent?: boolean }
  | { type: 'gain_charge'; chargeType: string }
  | { type: 'deal_damage'; damage: number; element: string; aoe?: number }
  | { type: 'apply_status'; status: string; duration: number; stacks?: number }
  | { type: 'remove_status'; status: string }
  | { type: 'spawn_effect'; effectId: string }
  | { type: 'leech'; stat: 'hp' | 'mana'; percent: number };

// ========================
// Modifier Source Types
// ========================

export interface ModifierSource {
  id: string;
  name: string;
  type: 'item' | 'passive' | 'skill' | 'buff' | 'base';
  modifiers: Modifier[];
  triggerEffects?: TriggerEffect[];
}

// ========================
// Main StatCalculator Class
// ========================

export class StatCalculator {
  private sources: Map<string, ModifierSource> = new Map();
  private cachedCollection: ModifierCollection | null = null;
  private cachedTriggers: Map<TriggerType, TriggerEffect[]> | null = null;

  /**
   * Add a modifier source
   */
  addSource(source: ModifierSource): void {
    this.sources.set(source.id, source);
    this.invalidateCache();
  }

  /**
   * Remove a modifier source
   */
  removeSource(sourceId: string): void {
    this.sources.delete(sourceId);
    this.invalidateCache();
  }

  /**
   * Clear all sources
   */
  clear(): void {
    this.sources.clear();
    this.invalidateCache();
  }

  /**
   * Invalidate cached calculations
   */
  private invalidateCache(): void {
    this.cachedCollection = null;
    this.cachedTriggers = null;
  }

  /**
   * Build the modifier collection from all sources
   */
  private buildCollection(): ModifierCollection {
    if (this.cachedCollection) return this.cachedCollection;

    const collection = new ModifierCollection();

    for (const source of this.sources.values()) {
      collection.addAll(source.modifiers);
    }

    this.cachedCollection = collection;
    return collection;
  }

  /**
   * Get final calculated value for a stat
   */
  calculate(stat: StatType, baseValue?: number): number {
    const collection = this.buildCollection();
    return collection.calculate(stat, baseValue);
  }

  /**
   * Get breakdown of modifiers for a stat (for tooltips)
   */
  getBreakdown(stat: StatType): ReturnType<ModifierCollection['getBreakdown']> {
    const collection = this.buildCollection();
    return collection.getBreakdown(stat);
  }

  /**
   * Get all trigger effects of a specific type
   */
  getTriggerEffects(trigger: TriggerType): TriggerEffect[] {
    if (!this.cachedTriggers) {
      this.cachedTriggers = new Map();

      for (const source of this.sources.values()) {
        if (source.triggerEffects) {
          for (const effect of source.triggerEffects) {
            const list = this.cachedTriggers.get(effect.trigger) || [];
            list.push(effect);
            this.cachedTriggers.set(effect.trigger, list);
          }
        }
      }
    }

    return this.cachedTriggers.get(trigger) || [];
  }

  /**
   * Get all modifiers from all sources
   */
  getAllModifiers(): Modifier[] {
    return this.buildCollection().getModifiers();
  }

  /**
   * Get modifiers grouped by source
   */
  getModifiersBySource(): Map<string, Modifier[]> {
    const result = new Map<string, Modifier[]>();

    for (const source of this.sources.values()) {
      result.set(source.id, [...source.modifiers]);
    }

    return result;
  }
}

// ========================
// Equipment Modifier Extraction
// ========================

/**
 * Convert equipment item mods to Modifiers
 */
export function extractEquipmentModifiers(item: EquipmentItem, source: string): Modifier[] {
  const modifiers: Modifier[] = [];

  // Combine all mods
  const allMods = [
    ...(item.implicit || []),
    ...(item.prefixes || []),
    ...(item.suffixes || []),
  ];

  for (const mod of allMods) {
    const modifier = convertItemMod(mod, source);
    if (modifier) {
      modifiers.push(modifier);
    }
  }

  return modifiers;
}

/**
 * Convert ItemMod to Modifier
 */
function convertItemMod(mod: ItemMod, source: string): Modifier | null {
  // Map item mod type to StatType
  const statType = mapModTypeToStat(mod.type);
  if (!statType) return null;

  // Determine modifier type from calcType
  const modType = mapCalcType(mod.calcType);

  return {
    stat: statType,
    type: modType,
    value: mod.value,
    source,
  };
}

/**
 * Map item mod type string to StatType
 */
function mapModTypeToStat(modType: string): StatType | null {
  // Direct mapping for most common types
  const directMap: Record<string, StatType> = {
    // Resources
    'maxHp': 'maxHp',
    'hp': 'maxHp',
    'maxMana': 'maxMana',
    'mana': 'maxMana',
    'energyShield': 'energyShield',
    'hpRegen': 'hpRegen',
    'manaRegen': 'manaRegen',

    // Attributes
    'strength': 'strength',
    'agility': 'agility',
    'spirit': 'spirit',

    // Offense
    'damage': 'damage',
    'attackDamage': 'attackDamage',
    'spellDamage': 'spellDamage',
    'dotDamage': 'dotDamage',
    'physicalDamage': 'physicalDamage',
    'odDamage': 'odDamage',
    'suDamage': 'suDamage',
    'gokDamage': 'gokDamage',
    'karanlikDamage': 'karanlikDamage',
    'elementalDamage': 'elementalDamage',
    'critChance': 'critChance',
    'critDamage': 'critDamage',
    'attackSpeed': 'attackSpeed',
    'castSpeed': 'castSpeed',
    'cooldownReduction': 'cooldownReduction',
    'projectileCount': 'projectileCount',
    'areaOfEffect': 'areaOfEffect',

    // Defense
    'armor': 'armor',
    'evasion': 'evasion',
    'blockChance': 'blockChance',
    'dodgeChance': 'dodgeChance',
    'dodgeCharges': 'dodgeCharges',

    // Resistances
    'physicalResist': 'physicalResist',
    'odResist': 'odResist',
    'suResist': 'suResist',
    'gokResist': 'gokResist',
    'karanlikResist': 'karanlikResist',
    'allResist': 'allResist',

    // Utility
    'moveSpeed': 'moveSpeed',
    'itemRarity': 'itemRarity',
    'itemQuantity': 'itemQuantity',
    'expGain': 'expGain',

    // Leech
    'lifeLeech': 'lifeLeech',
    'manaLeech': 'manaLeech',

    // Minions
    'minionDamage': 'minionDamage',
    'minionLife': 'minionLife',
    'minionSpeed': 'minionSpeed',
  };

  return directMap[modType] || null;
}

/**
 * Map ItemMod calcType to ModifierType
 */
function mapCalcType(calcType?: ModifierCalcType): ModifierType {
  if (!calcType) return 'flat';

  switch (calcType) {
    case 'flat': return 'flat';
    case 'increased': return 'increased';
    case 'more': return 'more';
    case 'reduced': return 'reduced';
    case 'less': return 'less';
    default: return 'flat';
  }
}

// ========================
// Passive Tree Integration
// ========================

/**
 * Convert passive bonuses to ModifierSource
 */
export function createPassiveSource(bonuses: Record<string, number>, sourceId: string = 'passives'): ModifierSource {
  const modifiers: Modifier[] = [];
  const triggerEffects: TriggerEffect[] = [];

  // Map passive bonus keys to stat types
  const bonusMapping: Record<string, { stat: StatType; type: ModifierType }> = {
    // Flat stats
    'flatStrength': { stat: 'strength', type: 'flat' },
    'flatAgility': { stat: 'agility', type: 'flat' },
    'flatSpirit': { stat: 'spirit', type: 'flat' },
    'flatHp': { stat: 'maxHp', type: 'flat' },
    'flatMana': { stat: 'maxMana', type: 'flat' },
    'flatArmor': { stat: 'armor', type: 'flat' },
    'flatEvasion': { stat: 'evasion', type: 'flat' },

    // Increased stats - general
    'percentDamage': { stat: 'damage', type: 'increased' },
    'percentPhysicalDamage': { stat: 'physicalDamage', type: 'increased' },
    'percentElementalDamage': { stat: 'elementalDamage', type: 'increased' },
    'percentHp': { stat: 'maxHp', type: 'increased' },
    'percentMana': { stat: 'maxMana', type: 'increased' },
    'percentArmor': { stat: 'armor', type: 'increased' },
    'percentEvasion': { stat: 'evasion', type: 'increased' },
    'percentAttackSpeed': { stat: 'attackSpeed', type: 'increased' },
    'percentCastSpeed': { stat: 'castSpeed', type: 'increased' },
    'percentMoveSpeed': { stat: 'moveSpeed', type: 'increased' },
    'percentMovementSpeed': { stat: 'moveSpeed', type: 'increased' },
    'percentCritChance': { stat: 'critChance', type: 'increased' },
    'percentCritDamage': { stat: 'critDamage', type: 'increased' },

    // Increased stats - elemental damage
    'percentFireDamage': { stat: 'odDamage', type: 'increased' },
    'percentLightningDamage': { stat: 'gokDamage', type: 'increased' },
    'percentColdDamage': { stat: 'suDamage', type: 'increased' },
    'percentChaosDamage': { stat: 'karanlikDamage', type: 'increased' },

    // Regen
    'percentHpRegen': { stat: 'hpRegen', type: 'increased' },
    'percentManaRegen': { stat: 'manaRegen', type: 'increased' },

    // Cooldown reduction (additive %)
    'percentCooldownReduction': { stat: 'cooldownReduction', type: 'increased' },

    // DoT / AoE / Projectiles
    'dotDamage': { stat: 'dotDamage', type: 'increased' },
    'areaOfEffect': { stat: 'areaOfEffect', type: 'increased' },
    'projectileCount': { stat: 'projectileCount', type: 'flat' },

    // Dodge charges
    'extraDodgeCharges': { stat: 'dodgeCharges', type: 'flat' },

    // Resistances
    'odResist': { stat: 'odResist', type: 'flat' },
    'suResist': { stat: 'suResist', type: 'flat' },
    'gokResist': { stat: 'gokResist', type: 'flat' },
    'karanlikResist': { stat: 'karanlikResist', type: 'flat' },
    'allResist': { stat: 'allResist', type: 'flat' },

    // Leech (PassiveBonuses uses lifeLeechPercent/manaLeechPercent keys)
    'lifeLeech': { stat: 'lifeLeech', type: 'flat' },
    'manaLeech': { stat: 'manaLeech', type: 'flat' },
    'lifeLeechPercent': { stat: 'lifeLeech', type: 'flat' },
    'manaLeechPercent': { stat: 'manaLeech', type: 'flat' },

    // Minions
    'minionDamage': { stat: 'minionDamage', type: 'increased' },
    'minionLife': { stat: 'minionLife', type: 'increased' },
  };

  for (const [key, value] of Object.entries(bonuses)) {
    if (value === 0) continue;

    const mapping = bonusMapping[key];
    if (mapping) {
      modifiers.push({
        stat: mapping.stat,
        type: mapping.type,
        value: value,
        source: sourceId,
      });
    }

    // Handle special passive effects that are trigger-based
    if (key === 'lifeOnKill' && value > 0) {
      triggerEffects.push({
        id: `passive_life_on_kill`,
        name: 'Life on Kill',
        trigger: 'on_kill',
        effect: { type: 'heal', amount: value, isPercent: false },
        source: sourceId,
      });
    }

    if (key === 'manaOnKill' && value > 0) {
      triggerEffects.push({
        id: `passive_mana_on_kill`,
        name: 'Mana on Kill',
        trigger: 'on_kill',
        effect: { type: 'restore_mana', amount: value, isPercent: false },
        source: sourceId,
      });
    }
  }

  return {
    id: sourceId,
    name: 'Passive Tree',
    type: 'passive',
    modifiers,
    triggerEffects,
  };
}

/**
 * Convert passive bonuses to Modifier array (reusable helper)
 */
export function convertBonusesToModifiers(bonuses: Record<string, number>, source: string): Modifier[] {
  const modifiers: Modifier[] = [];

  const bonusMapping: Record<string, { stat: StatType; type: ModifierType }> = {
    'flatStrength': { stat: 'strength', type: 'flat' },
    'flatAgility': { stat: 'agility', type: 'flat' },
    'flatSpirit': { stat: 'spirit', type: 'flat' },
    'flatHp': { stat: 'maxHp', type: 'flat' },
    'flatMana': { stat: 'maxMana', type: 'flat' },
    'flatArmor': { stat: 'armor', type: 'flat' },
    'flatEvasion': { stat: 'evasion', type: 'flat' },
    'percentDamage': { stat: 'damage', type: 'increased' },
    'percentPhysicalDamage': { stat: 'physicalDamage', type: 'increased' },
    'percentElementalDamage': { stat: 'elementalDamage', type: 'increased' },
    'percentHp': { stat: 'maxHp', type: 'increased' },
    'percentMana': { stat: 'maxMana', type: 'increased' },
    'percentArmor': { stat: 'armor', type: 'increased' },
    'percentEvasion': { stat: 'evasion', type: 'increased' },
    'percentAttackSpeed': { stat: 'attackSpeed', type: 'increased' },
    'percentCastSpeed': { stat: 'castSpeed', type: 'increased' },
    'percentMoveSpeed': { stat: 'moveSpeed', type: 'increased' },
    'percentMovementSpeed': { stat: 'moveSpeed', type: 'increased' },
    'percentCritChance': { stat: 'critChance', type: 'increased' },
    'percentCritDamage': { stat: 'critDamage', type: 'increased' },
    'percentFireDamage': { stat: 'odDamage', type: 'increased' },
    'percentLightningDamage': { stat: 'gokDamage', type: 'increased' },
    'percentColdDamage': { stat: 'suDamage', type: 'increased' },
    'percentChaosDamage': { stat: 'karanlikDamage', type: 'increased' },
    'percentHpRegen': { stat: 'hpRegen', type: 'increased' },
    'percentManaRegen': { stat: 'manaRegen', type: 'increased' },
    'percentCooldownReduction': { stat: 'cooldownReduction', type: 'increased' },
    'dotDamage': { stat: 'dotDamage', type: 'increased' },
    'areaOfEffect': { stat: 'areaOfEffect', type: 'increased' },
    'projectileCount': { stat: 'projectileCount', type: 'flat' },
    'extraDodgeCharges': { stat: 'dodgeCharges', type: 'flat' },
    'lifeLeechPercent': { stat: 'lifeLeech', type: 'flat' },
    'manaLeechPercent': { stat: 'manaLeech', type: 'flat' },
    'minionDamage': { stat: 'minionDamage', type: 'increased' },
    'minionLife': { stat: 'minionLife', type: 'increased' },
  };

  for (const [key, value] of Object.entries(bonuses)) {
    if (value === 0) continue;
    const mapping = bonusMapping[key];
    if (mapping) {
      modifiers.push({ stat: mapping.stat, type: mapping.type, value, source });
    }
  }

  return modifiers;
}

/**
 * Create a ModifierSource from raw modifiers and trigger effects (for new passive tree path)
 */
export function createPassiveModifierSource(
  modifiers: Modifier[],
  triggerEffects: TriggerEffect[] = [],
  sourceId: string = 'passives'
): ModifierSource {
  return { id: sourceId, name: 'Passive Tree', type: 'passive', modifiers, triggerEffects };
}

// ========================
// Effect Stone Integration
// ========================

/**
 * Effect stone types that can be socketed into skills
 */
export interface EffectStone {
  id: string;
  name: string;
  nameTr: string;
  type: 'support' | 'trigger' | 'enhancement';

  // Modifiers applied to the skill
  modifiers?: Partial<Record<StatType, { value: number; type: ModifierType }>>;

  // Trigger effects
  triggerEffects?: TriggerEffect[];

  // Skill modifications
  skillModifications?: {
    addedElement?: string;
    convertElement?: { from: string; to: string; percent: number };
    addProjectiles?: number;
    chainCount?: number;
    aoeMultiplier?: number;
    durationMultiplier?: number;
  };
}

/**
 * Convert effect stone to modifier source
 */
export function createEffectStoneSource(stone: EffectStone, skillId: string): ModifierSource {
  const modifiers: Modifier[] = [];
  const sourceId = `skill_${skillId}_stone_${stone.id}`;

  if (stone.modifiers) {
    for (const [stat, mod] of Object.entries(stone.modifiers)) {
      modifiers.push({
        stat: stat as StatType,
        type: mod.type,
        value: mod.value,
        source: sourceId,
      });
    }
  }

  return {
    id: sourceId,
    name: `${stone.name} (${skillId})`,
    type: 'skill',
    modifiers,
    triggerEffects: stone.triggerEffects?.map(e => ({ ...e, source: sourceId })),
  };
}

// ========================
// Standard Effect Stones Library
// ========================

export const EFFECT_STONES: Record<string, EffectStone> = {
  // Damage Support
  'added_fire': {
    id: 'added_fire',
    name: 'Added Fire',
    nameTr: 'Ek Od Hasarı',
    type: 'support',
    modifiers: {
      odDamage: { value: 25, type: 'flat' },
    },
  },

  'added_cold': {
    id: 'added_cold',
    name: 'Added Cold',
    nameTr: 'Ek Su Hasarı',
    type: 'support',
    modifiers: {
      suDamage: { value: 25, type: 'flat' },
    },
  },

  'added_lightning': {
    id: 'added_lightning',
    name: 'Added Lightning',
    nameTr: 'Ek Gök Hasarı',
    type: 'support',
    modifiers: {
      gokDamage: { value: 25, type: 'flat' },
    },
  },

  'brutality': {
    id: 'brutality',
    name: 'Brutality',
    nameTr: 'Vahşet',
    type: 'support',
    modifiers: {
      physicalDamage: { value: 40, type: 'more' },
    },
  },

  // Critical Support
  'increased_crit': {
    id: 'increased_crit',
    name: 'Increased Critical',
    nameTr: 'Artırılmış Kritik',
    type: 'support',
    modifiers: {
      critChance: { value: 50, type: 'increased' },
    },
  },

  'crit_damage': {
    id: 'crit_damage',
    name: 'Critical Damage',
    nameTr: 'Kritik Hasarı',
    type: 'support',
    modifiers: {
      critDamage: { value: 30, type: 'increased' },
    },
  },

  // Trigger Effects
  'life_leech': {
    id: 'life_leech',
    name: 'Life Leech',
    nameTr: 'Can Emme',
    type: 'trigger',
    triggerEffects: [{
      id: 'life_leech_on_hit',
      name: 'Leech Life',
      trigger: 'on_skill_hit',
      effect: { type: 'leech', stat: 'hp', percent: 2 },
    }],
  },

  'mana_leech': {
    id: 'mana_leech',
    name: 'Mana Leech',
    nameTr: 'Mana Emme',
    type: 'trigger',
    triggerEffects: [{
      id: 'mana_leech_on_hit',
      name: 'Leech Mana',
      trigger: 'on_skill_hit',
      effect: { type: 'leech', stat: 'mana', percent: 1 },
    }],
  },

  'on_kill_explode': {
    id: 'on_kill_explode',
    name: 'Explosion on Kill',
    nameTr: 'Öldürmede Patlama',
    type: 'trigger',
    triggerEffects: [{
      id: 'explode_on_kill',
      name: 'Corpse Explosion',
      trigger: 'on_kill',
      condition: { chance: 30 },
      effect: { type: 'deal_damage', damage: 50, element: 'physical', aoe: 60 },
    }],
  },

  // Enhancement
  'faster_attacks': {
    id: 'faster_attacks',
    name: 'Faster Attacks',
    nameTr: 'Hızlı Saldırılar',
    type: 'enhancement',
    modifiers: {
      attackSpeed: { value: 30, type: 'increased' },
    },
  },

  'faster_casting': {
    id: 'faster_casting',
    name: 'Faster Casting',
    nameTr: 'Hızlı Büyü',
    type: 'enhancement',
    modifiers: {
      castSpeed: { value: 30, type: 'increased' },
    },
  },

  'increased_area': {
    id: 'increased_area',
    name: 'Increased Area',
    nameTr: 'Artırılmış Alan',
    type: 'enhancement',
    modifiers: {
      areaOfEffect: { value: 40, type: 'increased' },
    },
  },

  'chain': {
    id: 'chain',
    name: 'Chain',
    nameTr: 'Zincir',
    type: 'enhancement',
    skillModifications: {
      chainCount: 2,
    },
  },

  'fork': {
    id: 'fork',
    name: 'Fork',
    nameTr: 'Çatal',
    type: 'enhancement',
    skillModifications: {
      addProjectiles: 2,
    },
  },
};

// ========================
// Exports
// ========================

export {
  createModifier,
  ModifierCollection,
} from './ModifierSystem';
